/** @file treeRows: the visible rows of the node tree (depth-first by parent, `Object.keys` order). */
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';

/**
 * One visible row: the node, its depth, whether it has children, its twin, IK chain length, hidden-in-data flag
 * (`hiddenAbove`: an ancestor has it), hit/fade group, socket names pointing at it and the flex-parent flag.
 */
export type TreeRow = {
  name: string; depth: number; hasChildren: boolean; mirror: string | null; ik: number; hidden: boolean;
  hiddenAbove: boolean; group: number | null; sockets: string[]; flexParent: boolean;
};

/** What `treeRows` needs besides the nodes: socket name -> node (for the socket badge), the fold set, a visible filter. */
export type TreeRowsOptions = {
  collapsed?: ReadonlySet<string>;
  sockets?: Record<string, string>;
  /** When set, only these nodes get rows (matches and their ancestors) and folding is ignored. */
  visible?: ReadonlySet<string> | null;
};

/** Child names by parent name (null = roots); a node whose parent is missing is a root. */
function childrenByParent(nodes: Record<string, PuppetNode>): Map<string | null, string[]> {
  const children = new Map<string | null, string[]>();
  for (const [name, node] of Object.entries(nodes)) {
    const parent = node.parent !== undefined && node.parent in nodes ? node.parent : null;
    children.set(parent, [...(children.get(parent) ?? []), name]);
  }
  return children;
}

/** Socket names by the node they point at. */
function socketsByNode(sockets: Record<string, string>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [socket, node] of Object.entries(sockets)) out.set(node, [...(out.get(node) ?? []), socket]);
  return out;
}

/** The row of one node from its data plus the tree context (`depth`, `hiddenAbove`, `hasChildren`, socket names). */
function rowOf(name: string, node: PuppetNode, ctx: Pick<TreeRow, 'depth' | 'hiddenAbove' | 'hasChildren'> & { sockets: string[] | undefined }): TreeRow {
  return {
    ...ctx, sockets: ctx.sockets ?? [], name, mirror: node.mirror ?? null, ik: node.ik ?? 0, hidden: node.hidden === true, group: node.group ?? null,
    flexParent: node.flexParent === true,
  };
}

/**
 * Rows of `nodes` nested by `parent`, siblings in `Object.keys` order; a node whose parent is missing is a root;
 * the descendants of a `collapsed` node are left out (unless a `visible` filter is active); a parent cycle is cut
 * where it closes.
 */
export function treeRows(nodes: Record<string, PuppetNode>, options: TreeRowsOptions = {}): TreeRow[] {
  const { collapsed = new Set<string>(), sockets = {}, visible = null } = options;
  const socketsOf = socketsByNode(sockets);
  const children = childrenByParent(nodes);
  const rows: TreeRow[] = [];
  const shown = (n: string): boolean => visible === null || visible.has(n);
  const seen = new Set<string>();
  const visit = (name: string, depth: number, hiddenAbove: boolean): void => {
    const node = nodes[name];
    if (node === undefined || seen.has(name)) return;
    seen.add(name);
    if (!shown(name)) return;
    const kids = (children.get(name) ?? []).filter(shown);
    rows.push(rowOf(name, node, { depth, hiddenAbove, hasChildren: kids.length > 0, sockets: socketsOf.get(name) }));
    if (visible === null && collapsed.has(name)) return;
    for (const kid of kids) visit(kid, depth + 1, hiddenAbove || node.hidden === true);
  };
  for (const root of children.get(null) ?? []) visit(root, 0, false);
  return rows;
}
